import { useAction, useMutation, useQuery } from "convex/react";
import { useCallback } from "react";

import { api } from "@convex/_generated/api";
import {
  studioAppNotFound,
  studioShareFailed,
  studioTooManyApps,
  studioUnshareFailed,
} from "@domain/user-errors";
import { errorCodeText } from "@/lib/convexErrors";
import { logMutationError } from "@/lib/logger";

/**
 * Studio share hooks (studio spec §4.4) — components stay Convex-free.
 * Shares live in the `studio_shares` table, so `useShareForApp` is a plain
 * reactive `useQuery` (unlike the action-backed app reads in
 * `useStudioApps`): a publish/unshare lands in the strip without a manual
 * refresh.
 *
 * `publish` sends ONLY `{ appDir }` (path-as-identity, spec M10-adjacent):
 * ownership is proven server-side by probing the caller's OWN vault prefix,
 * and the public URL is composed server-side from `STUDIO_APEX_DOMAIN` — the
 * frontend never composes share URLs and never supplies publish content.
 */

/** Owner's view of one app's share state (`getForApp`; inactive rows are
 *  filtered server-side — a revoked share reads as "no share"). */
export interface ShareForApp {
  shareName: string;
  url: string;
  active: boolean;
}

/** Machine-readable publish-failure class (spec §5). */
export type StudioShareErrorCode =
  | "APP_LIMIT_EXCEEDED"
  | "NOT_FOUND"
  | "INVALID_APP_PATH"
  | "SHARE_NAME_EXHAUSTED"
  | "UNKNOWN";

// Mutations/actions throw `ConvexError(CODE)` — see `errorCodeText` for why a
// plain `Error` message can't be matched here (redacted in prod).
function codeOf(error: unknown): StudioShareErrorCode {
  const msg = errorCodeText(error);
  if (msg.includes("APP_LIMIT_EXCEEDED")) return "APP_LIMIT_EXCEEDED";
  if (msg.includes("SHARE_NAME_EXHAUSTED")) return "SHARE_NAME_EXHAUSTED";
  if (msg.includes("INVALID_APP_PATH")) return "INVALID_APP_PATH";
  if (msg.includes("NOT_FOUND")) return "NOT_FOUND";
  return "UNKNOWN";
}

function publishErrorCopy(code: StudioShareErrorCode): string {
  switch (code) {
    case "APP_LIMIT_EXCEEDED":
      return studioTooManyApps();
    case "NOT_FOUND":
    case "INVALID_APP_PATH":
      return studioAppNotFound();
    // SHARE_NAME_EXHAUSTED and transport failures are genuine failures →
    // suffixed generic copy.
    default:
      return studioShareFailed();
  }
}

/** Thrown by publish/unpublish so callers surface copy inline. */
export class StudioShareError extends Error {
  readonly userMessage: string;
  readonly code: StudioShareErrorCode;
  readonly reason: unknown;
  constructor(userMessage: string, code: StudioShareErrorCode, reason: unknown) {
    super(userMessage);
    this.name = "StudioShareError";
    this.userMessage = userMessage;
    this.code = code;
    this.reason = reason;
  }
}

/**
 * Live share state for one app; `null` appDir skips (no app in focus).
 * `undefined` = loading, `null` = not shared.
 */
export function useShareForApp(
  appDir: string | null,
): ShareForApp | null | undefined {
  return useQuery(api.studioShares.getForApp, appDir === null ? "skip" : { appDir });
}

/** Publish (or re-share) an app. Sends ONLY `{ appDir }` — the arg-surface
 *  pin lives in `useStudioShares.test.tsx`. */
export function usePublishAppShare() {
  const publishAction = useAction(api.studioShareActions.publish);
  return useCallback(
    async ({ appDir }: { appDir: string }): Promise<{ shareName: string; url: string }> => {
      try {
        return await publishAction({ appDir });
      } catch (err) {
        logMutationError({
          mutation: "studioShareActions.publish",
          args: { appDir },
          error: err,
        });
        const code = codeOf(err);
        throw new StudioShareError(publishErrorCopy(code), code, err);
      }
    },
    [publishAction],
  );
}

/** Revoke an app's share. Idempotent server-side (`active: false` patch —
 *  the name stays reserved forever; the serve path 410s it). */
export function useUnpublishAppShare() {
  const unshareMutation = useMutation(api.studioShares.unshare);
  return useCallback(
    async ({ appDir }: { appDir: string }): Promise<void> => {
      try {
        await unshareMutation({ appDir });
      } catch (err) {
        logMutationError({
          mutation: "studioShares.unshare",
          args: { appDir },
          error: err,
        });
        throw new StudioShareError(studioUnshareFailed(), "UNKNOWN", err);
      }
    },
    [unshareMutation],
  );
}
